import { AccordionAnimation } from "components/AccordionAnimation";
import { TableUI } from "components/design/TableUI";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import {
  InvoiceAmountColumn,
  InvoiceDateColumn,
  InvoiceNumberColumn,
  SentToColumn,
} from "components/InvoiceColumns/InvoiceColumns";
import { billColumns } from "components/VendorBills/BillList";
import { AnimatePresence } from "framer-motion";
import { useQuery } from "hooks/useQuery";
import { useEffect, useState } from "react";
import { getCoreRowModel, useReactTable } from "react-table-8.10.7";
import {
  JournalEntryResult,
  useGetLedgerLinkedInvoicesQuery,
} from "store/apis/generalLedger";
import { currency } from "utils/Currency";
import { useDebounce } from "utils/debounce";
import { pluralize } from "utils/pluralize";

type LinkedInvoicesProps = {
  ledgerEntryId: string;
  entityId: string;
};

const columns = [
  { ...InvoiceDateColumn, size: 10 },
  { ...InvoiceNumberColumn, size: 15 },
  { ...SentToColumn, size: 40 },
  { ...InvoiceAmountColumn, size: 40 },
];

export default function LinkedInvoices({
  ledgerEntryId,
  entityId,
}: LinkedInvoicesProps) {
  const [displayCurrency, setDisplayCurrency] = useState("USD");

  const billCollumns = [
    billColumns.bill_date(10),
    billColumns.bill_number(15),
    billColumns.entity_merchant_data(40),
    billColumns.due_balance(40),
  ];

  const [pageNum, setPageNum] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm);
  useEffect(() => {
    setPageNum(1);
  }, [debouncedSearchTerm]);

  const query = useQuery();
  const pageSize = Number(query.get("page_size")) || 20;
  useEffect(() => {
    setPageNum(1);
  }, [pageSize]);

  const {
    data: linkedInvoices,
    isLoading,
    isSuccess,
    isFetching,
  } = useGetLedgerLinkedInvoicesQuery(
    {
      entityId,
      ledgerEntryId: ledgerEntryId,
      page_num: pageNum,
      page_size: pageSize,
      search_term: debouncedSearchTerm || undefined,
    },
    {
      skip: !ledgerEntryId || !entityId,
    }
  );

  const {
    linked_invoices = [],
    per_page = 10,
    total_count = 0,
    total_pages = 1,
    total_amount = 0,
  } = linkedInvoices || {};

  const [hasAnyLinkedInvoices, setHasAnyLinkedInvoices] = useState(false);
  useEffect(() => {
    if (!debouncedSearchTerm && total_count > 0) {
      setHasAnyLinkedInvoices(true);
    }
  }, [debouncedSearchTerm, total_count]);

  const recordType = linked_invoices[0]?.financial_record_type;
  const table = useReactTable({
    data: linked_invoices,
    getCoreRowModel: getCoreRowModel(),
    // @ts-ignore
    columns: recordType === "BILL" ? billCollumns : columns,
    defaultColumn: {
      minSize: 10,
    },
    meta: {
      displayCurrency,
      setDisplayCurrency,
      openInNewTab: true,
    },
  });

  if (!hasAnyLinkedInvoices) {
    return null;
  }

  return (
    <Accordion.Root type="single" collapsible>
      <Accordion.Item value="LINKED_INVOICES">
        <Accordion.Trigger disabled={isLoading || !isSuccess}>
          Linked to{" "}
          {pluralize(
            total_count || 0,
            recordType === "BILL" ? "bill" : "invoice",
            recordType === "BILL" ? "bills" : "invoices"
          )}
          {total_amount ? ` • ${currency({ amount: total_amount })}` : ""}
        </Accordion.Trigger>
        <Accordion.Content>
          <AnimatePresence>
            <AccordionAnimation duration={0.3}>
              <div className="t-flex t-flex-col t-gap-3">
                <div className="t-flex t-items-center t-justify-between t-gap-3">
                  <Search
                    placeholder="Search by invoice/bill number or customer"
                    className="t-w-[45%]"
                    defaultValue={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    isLoading={isFetching}
                  />
                  <PaginationHandler
                    page_num={pageNum}
                    per_page={per_page}
                    total_count={total_count}
                    total_pages={total_pages}
                    onPageNumChange={setPageNum}
                    showOptions
                    isLoading={isFetching}
                  />
                </div>
                {linked_invoices.length === 0 ? (
                  <div className="t-py-6 t-text-center t-text-text-30">
                    No invoices match &quot;{debouncedSearchTerm}&quot;
                  </div>
                ) : (
                  <div className="t-overflow-hidden">
                    <TableUI table={table} />
                  </div>
                )}
              </div>
            </AccordionAnimation>
          </AnimatePresence>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
}
